import React from 'react';
import { FaChartLine, FaEdit, FaEye, FaTrash, FaArrowUp, FaArrowDown, FaUserTie } from 'react-icons/fa';
import {
  CompetitorCard as StyledCompetitorCard,
  CompetitorHeader,
  CompetitorAvatar,
  CompetitorImage,
  CompetitorInfo,
  CompetitorName,
  CompetitorParty,
  CompetitorStats,
  StatBadge,
  CompetitorActions,
  IconButton,
  CompetitorBody,
  CompetitorSection,
  SectionTitle,
  StrengthWeaknessList,
  StrengthWeaknessCard,
  CardTitle,
  ItemsList,
  ProgressBar,
  Progress,
} from './CompetitorsStyles';
import CompetitorNewsWidget from './CompetitorNewsWidget';

function getInitials(name) {
  if (!name) return '??';
  return name
    .replace(/\([^)]*\)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('');
}

const Initials = ({ name }) => (
  <span
    style={{
      fontFamily: "'JetBrains Mono', 'Consolas', monospace",
      fontSize: '1.05rem',
      fontWeight: 600,
      color: 'var(--btn-primary)',
      letterSpacing: '0.04em',
    }}
  >
    {getInitials(name)}
  </span>
);

const CompetitorCard = ({ competitor }) => {
  const {
    slug,
    name,
    party,
    leaders,
    votingIntention,
    avatarColor,
    image,
    profile,
    description,
    strengths,
    weaknesses,
    mainTopics,
  } = competitor || {};

  const profileText = profile || description;
  const topics = Array.isArray(mainTopics) ? mainTopics : [];

  return (
    <StyledCompetitorCard>
      <CompetitorHeader>
        <CompetitorAvatar borderColor={avatarColor}>
          {image ? (
            <CompetitorImage src={image} alt={`Foto de ${name}`} />
          ) : (
            <Initials name={name} />
          )}
        </CompetitorAvatar>

        <CompetitorInfo>
          <CompetitorName>{name}</CompetitorName>
          <CompetitorParty>{party || '—'}</CompetitorParty>
          <CompetitorStats>
            {typeof votingIntention === 'number' && (
              <StatBadge color={avatarColor}>
                <FaChartLine /> {votingIntention}% intención
              </StatBadge>
            )}
            {leaders && (
              <StatBadge>
                <FaUserTie /> {leaders.split(',')[0].trim()}
              </StatBadge>
            )}
          </CompetitorStats>
        </CompetitorInfo>

        <CompetitorActions>
          <IconButton aria-label="Editar"><FaEdit /></IconButton>
          <IconButton aria-label="Ver detalle"><FaEye /></IconButton>
          <IconButton aria-label="Eliminar"><FaTrash /></IconButton>
        </CompetitorActions>
      </CompetitorHeader>

      <CompetitorBody>
        {profileText && (
          <CompetitorSection>
            <SectionTitle>Perfil del competidor</SectionTitle>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0, fontSize: '0.92rem' }}>
              {profileText}
            </p>
          </CompetitorSection>
        )}

        {strengths && weaknesses && (
          <CompetitorSection>
            <SectionTitle>Fortalezas y debilidades</SectionTitle>
            <StrengthWeaknessList>
              <StrengthWeaknessCard color="var(--alert-success)">
                <CardTitle color="var(--alert-success)">
                  <FaArrowUp /> Fortalezas
                </CardTitle>
                <ItemsList>
                  {strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ItemsList>
              </StrengthWeaknessCard>

              <StrengthWeaknessCard color="var(--alert-error)">
                <CardTitle color="var(--alert-error)">
                  <FaArrowDown /> Debilidades
                </CardTitle>
                <ItemsList>
                  {weaknesses.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ItemsList>
              </StrengthWeaknessCard>
            </StrengthWeaknessList>
          </CompetitorSection>
        )}

        {topics.length > 0 && typeof topics[0] === 'object' && (
          <CompetitorSection>
            <SectionTitle>Temas principales</SectionTitle>
            {topics.map((topic, index) => (
              <div key={index} style={{ marginBottom: index < topics.length - 1 ? '0.7rem' : 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.82rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span>{topic.name}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', 'Consolas', monospace", color: 'var(--text-primary)' }}>
                    {topic.value}%
                  </span>
                </div>
                <ProgressBar>
                  <Progress value={topic.value} color={avatarColor} />
                </ProgressBar>
              </div>
            ))}
          </CompetitorSection>
        )}

        {topics.length > 0 && typeof topics[0] === 'string' && (
          <CompetitorSection>
            <SectionTitle>Temas principales</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {topics.map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontFamily: "'JetBrains Mono', 'Consolas', monospace",
                    fontSize: '0.72rem',
                    padding: '0.2rem 0.55rem',
                    border: '1px solid rgba(0, 180, 216, 0.25)',
                    borderRadius: '3px',
                    color: '#cfe9f4',
                    background: 'rgba(0, 180, 216, 0.06)',
                  }}
                >
                  #{t}
                </span>
              ))}
            </div>
          </CompetitorSection>
        )}

        {slug && <CompetitorNewsWidget slug={slug} />}
      </CompetitorBody>
    </StyledCompetitorCard>
  );
};

export default CompetitorCard;
